import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  ApiError,
  apiGet,
  getTokens,
  login as apiLogin,
  logout as apiLogout,
  onAuthChange,
  onAccessSuspended,
  switchSeat as apiSwitchSeat,
  verifyMfa as apiVerifyMfa,
} from "lib/apiClient";
import { clearRememberedSeat, rememberSeat, seatHomePath, seatRequest, setSwitchNotice } from "lib/seats";

const AuthContext = createContext(null);

const SUSPENDED_MESSAGES = {
  TENANT_SUSPENDED: "This organisation's account is suspended. Contact the platform administrator.",
  PARTNER_ACCESS_SUSPENDED: "This partner account is suspended or closed. Contact the organisation that manages your partnership.",
};

export function suspendedAccessMessage(code) {
  return SUSPENDED_MESSAGES[code] || null;
}

export function AuthProvider({ children }) {
  const [tokens, setTokens] = useState(getTokens());
  const [me, setMe] = useState(null);
  const [portal, setPortal] = useState(null);
  const [seatsInfo, setSeatsInfo] = useState({ seats: [], platformAdmin: false });
  const [status, setStatus] = useState("loading");
  const [authNotice, setAuthNotice] = useState(null);

  useEffect(() => onAuthChange(setTokens), []);

  const signOutSuspended = useCallback((code) => {
    setAuthNotice(suspendedAccessMessage(code));
    apiLogout();
    setMe(null);
    setPortal(null);
    setSeatsInfo({ seats: [], platformAdmin: false });
    setStatus("signed-out");
  }, []);

  useEffect(() => onAccessSuspended(signOutSuspended), [signOutSuspended]);

  const loadMe = useCallback(async () => {
    if (!getTokens()?.accessToken) {
      setMe(null);
      setPortal(null);
      setSeatsInfo({ seats: [], platformAdmin: false });
      setStatus("signed-out");
      return null;
    }
    try {
      const result = await apiGet("/auth/me");
      let context = null;
      let seatList = null;
      let suspendedCode = null;
      if (!result?.user?.mustChangePassword) {
        [context, seatList] = await Promise.all([
          apiGet("/portal/context").catch((err) => {
            if (err?.status === 403 && suspendedAccessMessage(err.code)) suspendedCode = err.code;
            return null;
          }),
          apiGet("/portal/seats").catch(() => null),
        ]);
      }
      if (suspendedCode) {
        signOutSuspended(suspendedCode);
        return suspendedCode;
      }
      setMe(result);
      setPortal(context);
      setSeatsInfo({ seats: seatList?.seats ?? [], platformAdmin: Boolean(seatList?.platformAdmin) });
      setStatus("signed-in");
      return null;
    } catch {
      setMe(null);
      setPortal(null);
      setSeatsInfo({ seats: [], platformAdmin: false });
      setStatus("signed-out");
      return null;
    }
  }, [signOutSuspended]);

  const loadAfterSignIn = useCallback(async () => {
    const suspendedCode = await loadMe();
    if (suspendedCode) throw new ApiError(suspendedAccessMessage(suspendedCode), 403, suspendedCode);
  }, [loadMe]);

  useEffect(() => {
    loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens]);

  const login = useCallback(
    async (email, password, seat) => {
      let result;
      let seatUsed = seat || null;
      try {
        result = await apiLogin(email, password, seatUsed ? seatRequest(seatUsed) : undefined);
      } catch (err) {
        const retryWithoutSeat = err?.code === "CLIENT_PORTAL_ACCESS_DENIED" || Boolean(suspendedAccessMessage(err?.code));
        if (!seatUsed || !retryWithoutSeat) throw err;
        clearRememberedSeat();
        seatUsed = null;
        result = await apiLogin(email, password);
      }
      result = { ...result, seatUsed };
      if (result.mfaRequired) return result;
      setAuthNotice(null);
      await loadAfterSignIn();
      return result;
    },
    [loadAfterSignIn],
  );

  const verifyMfa = useCallback(
    async (challengeToken, code) => {
      const result = await apiVerifyMfa(challengeToken, code);
      setAuthNotice(null);
      await loadAfterSignIn();
      return result;
    },
    [loadAfterSignIn],
  );

  const switchSeat = useCallback(async (seat) => {
    await apiSwitchSeat(seatRequest(seat));
    rememberSeat(seat);
    setSwitchNotice(seat);
    window.location.assign(seatHomePath(seat.kind));
  }, []);

  const logout = useCallback(() => {
    apiLogout();
    setMe(null);
    setPortal(null);
    setSeatsInfo({ seats: [], platformAdmin: false });
    setStatus("signed-out");
  }, []);

  const activeTenantId = portal?.tenant?.id ?? null;

  const hasPermission = useCallback(
    (permissionKey, tenantId = activeTenantId) => {
      if (!me) return false;
      return (me.grants ?? []).some(
        (g) => g.permissionKey === permissionKey && (g.tenantId == null || (tenantId != null && g.tenantId === tenantId)),
      );
    },
    [me, activeTenantId],
  );

  const clearAuthNotice = useCallback(() => setAuthNotice(null), []);

  const isPartner = portal?.kind === "PARTNER";
  const isTenantAdmin = portal?.kind === "TENANT" && Boolean(portal?.canManage) && hasPermission("partner.manage");

  const value = useMemo(
    () => ({
      status,
      user: me?.user ?? null,
      grants: me?.grants ?? [],
      portal,
      seats: seatsInfo.seats,
      platformAdmin: seatsInfo.platformAdmin,
      isPartner,
      isTenantAdmin,
      hasPermission,
      authNotice,
      clearAuthNotice,
      login,
      verifyMfa,
      switchSeat,
      logout,
      refresh: loadMe,
    }),
    [status, me, portal, seatsInfo, isPartner, isTenantAdmin, hasPermission, authNotice, clearAuthNotice, login, verifyMfa, switchSeat, logout, loadMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}

export function organisationLabel(portal) {
  if (!portal?.tenant) return "";
  if (portal.kind === "PARTNER" && portal.partner) return `${portal.partner.name} · a partner of ${portal.tenant.name}`;
  return portal.tenant.name;
}
